import Navbar from "../Common/Navbar";
import Sidebar from "../Common/Sidebar";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserDetails } from "../../ReduxToolKit/Slice/UserSlice";
import { fetchApprovedStores } from "../../ReduxToolKit/Slice/StoreSlice";
import { fetchApprovedMerchants } from "../../ReduxToolKit/Slice/MerchantsSlice";
import { getUserCookies } from "../../utils/Cookies";
import { jwtDecode } from "jwt-decode";
import { useEffect, useRef } from "react";
import useLanguage from "../../services/useLanguage";
import ChatBot from "../Common/ChatBot/ChatBot";
import Footer from "../Common/Footer";
import { Navigate, useLocation } from "react-router-dom";
import {
  getAllowedModuleKeysFromProfile,
  getPostLoginPathFromProfile,
  effectiveHighestRole,
  isPathAllowedByModulePermissions,
  userHasScopedModulePermissions,
} from "../../utils/modulePermissionSidebar"
import {
  canAccessOrganizationRoutes,
  isOrganizationRoute,
} from "../../utils/organizationAccess";
import { getLocalizedPath } from "../../utils/authLanguageRoutes";
import { getAppLanguage } from "../../utils/getAppLanguage";

const MainLayout = ({ children }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const profileUser = user.user;
  const isOrgOwner = useSelector((state) => state.organization.isOwner);
  const location = useLocation();
  const { language, handleLanguageChange } = useLanguage();
  const hasFetchedMerchants = useRef(false);

  const allowedKeys = profileUser
    ? getAllowedModuleKeysFromProfile(profileUser)
    : null;
  const useProfileModuleFilter = userHasScopedModulePermissions(profileUser);
  const isOrgOwnerRoute =
    isOrganizationRoute(location.pathname) &&
    canAccessOrganizationRoutes(profileUser, isOrgOwner);
  const mustRedirectFromDisallowedRoute =
    profileUser &&
    useProfileModuleFilter &&
    allowedKeys &&
    !isOrgOwnerRoute &&
    !isPathAllowedByModulePermissions(location.pathname, allowedKeys);

  // Change dir based on language
  useEffect(() => {
    dispatch(fetchUserDetails());
    dispatch(fetchApprovedStores());
  }, [dispatch]);

  // Fetch merchants once for platform / profile market admin or JWT market viewer (Stores needs merchant list)
  useEffect(() => {
    const token = getUserCookies()?.token;
    const decoded = token ? jwtDecode(token) : null;
    const highest_role = decoded?.highest_role;
    const market_role = decoded?.market_role;
    const effective = effectiveHighestRole(profileUser, highest_role);

    if (
      !hasFetchedMerchants.current &&
      (effective === "SUPERADMIN" ||
        market_role === "2" ||
        market_role === "3")
    ) {
      hasFetchedMerchants.current = true;
      dispatch(fetchApprovedMerchants());
    }
  }, [dispatch, profileUser]);

  if (user.loading) {
    return (
      <>
        <div
          className="d-flex justify-content-center align-items-center "
          style={{
            height: "100vh",
          }}
        >
          <svg
            width="58"
            height="68"
            viewBox="0 0 58 68"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              className="animate-fill"
              d="M35.4171 67.0648L42.4658 60.0162L35.4171 52.9675L28.3233 59.9917L28.3478 60.0162L28.3233 60.0406L35.4171 67.0648Z"
              fill="#504943"
            />
            <path
              className="animate-fill"
              d="M24.6791 60.0162L24.7128 60.0495L17.6975 67.0648L10.579 60.0162L17.6975 52.9675L24.7128 59.9828L24.6791 60.0162Z"
              fill="#504943"
            />
            <path
              className="animate-fill"
              d="M1.12726 34.5154V38.5304C1.12726 40.6436 1.90211 42.4751 3.45178 44.0952C5.00145 45.7153 6.83287 46.4901 9.01649 46.4901H41.2992C45.2438 46.4901 50.5233 46.4901 53.4185 49.3853C56.0247 51.9916 57.9858 55.1542 57.9858 55.1542H41.2054L35.426 49.3747L29.5893 55.1542H23.4858L17.7065 49.3749L11.8696 55.1542H11.2001C3.73353 54.802 0.000236511 50.5052 0.000236511 42.1933V30.7117C0.0706749 28.6689 0.211552 26.9079 0.563751 25.3583C1.05682 23.1042 2.67693 21.2023 5.42408 19.7231C7.04419 18.8074 8.80518 18.3143 10.7071 18.3143C12.0454 18.3143 13.5951 18.596 15.4969 19.1596C19.3711 20.5684 23.3862 21.9067 27.4717 23.1042C31.5571 24.3721 35.2904 25.1469 38.6715 25.4287H39.5168C46.7017 25.4287 51.9142 22.4702 55.1544 16.5533C55.7884 15.4967 56.0701 14.581 56.0701 13.8766C56.0701 12.5382 55.577 11.4112 54.5909 10.2842C53.6047 9.29802 51.9846 8.73451 49.6601 8.73451L10.2844 8.73451C5.00144 8.73451 1.62035 10.1433 0.000236511 13.0313C0.000236511 4.71945 3.73353 0.352197 11.1297 0L47.1947 1.72733e-06C50.2236 0.0704419 52.689 1.05659 54.5205 3.09934C56.3519 5.14209 57.4085 7.53703 57.6198 10.4251L57.6391 10.6169C57.7025 11.2456 57.7607 11.8215 57.7607 12.3974C57.7607 13.0313 57.6902 13.6653 57.6198 14.2992C57.5494 14.8628 57.338 15.7785 56.9154 16.9759C54.8022 22.8929 51.7029 26.9079 47.6878 29.0915C42.9683 31.6978 38.2489 33.0362 33.5295 33.0362C30.7823 33.0362 27.3308 32.2613 23.2453 30.6412C16.9057 27.9645 11.6932 26.6262 7.53727 26.6262C5.42409 26.6262 3.73355 27.6123 2.3952 29.5846C1.54992 30.8525 1.12726 32.4726 1.12726 34.5154Z"
              fill="#504943"
            />
          </svg>
        </div>
      </>
    );
  }

  if (mustRedirectFromDisallowedRoute) {
    return (
      <Navigate
        to={getLocalizedPath(
          getPostLoginPathFromProfile(profileUser),
          getAppLanguage(),
        )}
        replace
      />
    );
  }

  return (
    <>
      <div className="d-flex vh-100">
          {/* Sidebar with fixed height */}
          <Sidebar />

          {/* Main Content */}
          <div
            className="d-flex flex-column flex-grow-1"
            style={{ minWidth: 0 }}
          >
            {/* Navbar - Stays fixed at the top */}
            <Navbar
              currentLanguage={language}
              toggleLanguage={handleLanguageChange}
            />

            {/* Scrollable Content */}
            <div className="flex-grow-1 overflow-auto bg-F8F8F8 pxy-20-12">
              {/* <Outlet /> */}
              {children}
            </div>
            <Footer />
          </div>
          <ChatBot />
      </div>
    </>
  );
};

export default MainLayout;
